/**
 * AI-Assisted Resume API Client
 *
 * Wraps the existing apiClient for all AI-powered resume endpoints. Unlike
 * standard CRUD endpoints that throw on errors, AI endpoints use a non-blocking
 * pattern: they return either `{ data }` or `{ error }` to let the UI degrade
 * gracefully when the AI subsystem is unavailable or returns an error.
 *
 * Endpoints:
 * - POST /api/v1/ai/enrich-company       -- Company data enrichment
 * - POST /api/v1/ai/generate-content     -- Resume content generation
 * - POST /api/v1/ai/personalize-template -- Template recommendations
 *
 * @see ADR-055 for AI-assisted resume feature architecture
 */
import { apiClient } from './api.client';
import { logger } from './logger.client';
import { useAuthStore } from '../state/useAuthStore';
import type {
  EnrichCompanyRequest,
  EnrichCompanyData,
  GenerateContentRequest,
  GenerateContentData,
  PersonalizeTemplateRequest,
  PersonalizeTemplateData,
  ScoreResumeRequest,
  ScoreResumeData,
  GenerateCoverLetterRequest,
  GenerateCoverLetterData,
  ExtractAchievementsRequest,
  ExtractAchievementsData,
  AiResponse,
} from '../types/ai.types';

const log = logger.child({ module: 'aiApi' });

/**
 * Server envelope shape for successful AI responses.
 * The server wraps AI results in `{ data: T }` on success,
 * or returns `{ error: string }` when the AI fails gracefully.
 */
interface AiSuccessEnvelope<T> {
  data: T;
}

interface AiErrorEnvelope {
  error: string;
}

/** User-friendly message when the AI subsystem is entirely unavailable (HTTP 503). */
const AI_UNAVAILABLE_MESSAGE = 'AI features are not available';

/**
 * Execute an AI API request with graceful error handling.
 *
 * Unlike standard apiClient calls that throw on HTTP errors, this wrapper
 * catches failures and returns them as `{ error }` responses so the UI can
 * degrade gracefully rather than showing unhandled error states.
 *
 * Handles three response scenarios:
 * 1. HTTP 200 with `{ data }` -- AI succeeded, returns `{ data }`
 * 2. HTTP 200 with `{ error }` -- AI failed gracefully, returns `{ error }`
 * 3. HTTP 503 or other errors -- returns `{ error }` with friendly message
 *
 * @param url  - API endpoint path
 * @param body - Request payload to serialize as JSON
 * @returns Discriminated union: `{ data }` on success or `{ error }` on failure
 */
async function aiRequest<T>(
  url: string,
  body: unknown,
): Promise<AiResponse<T>> {
  try {
    /**
     * The apiClient throws for non-2xx responses, so we need to use raw
     * fetch for 503 detection. However, to maintain auth token handling
     * and refresh logic from apiClient, we use apiClient and catch the
     * thrown error for non-2xx cases.
     *
     * For 2xx responses, the server returns either:
     *   { data: T }   -- AI succeeded
     *   { error: '...' } -- AI failed gracefully
     */
    const response = await apiClient<AiSuccessEnvelope<T> | AiErrorEnvelope>(
      url,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );

    // Check if the server returned a graceful AI error (HTTP 200 but error payload)
    if ('error' in response && typeof response.error === 'string') {
      log.warn('AI request returned error', { url, error: response.error });
      return { error: response.error };
    }

    // Successful AI response with data
    if ('data' in response && response.data !== undefined) {
      return { data: response.data };
    }

    // Unexpected response shape -- treat as error
    log.error('AI response has unexpected shape', { url, response });
    return { error: 'Unexpected response from AI service' };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';

    // Detect 503 from the error message pattern set by apiClient
    if (message.includes('503')) {
      log.warn('AI service unavailable (503)', { url });
      return { error: AI_UNAVAILABLE_MESSAGE };
    }

    log.error('AI request failed', { url, error: message });
    return { error: message };
  }
}

/**
 * AI API client -- groups all AI-assisted resume endpoints.
 *
 * All methods return `AiResponse<T>` which is a discriminated union:
 * - Success: `{ data: T, error?: never }`
 * - Failure: `{ error: string, data?: never }`
 *
 * Callers should check for `result.error` before accessing `result.data`.
 *
 * @example
 * ```typescript
 * const result = await aiApi.enrichCompany({ company_name: 'Acme Corp' });
 * if (result.error) {
 *   showToast(`AI unavailable: ${result.error}`);
 *   return;
 * }
 * // result.data is typed as EnrichCompanyData
 * console.log(result.data.industry);
 * ```
 */
export const aiApi = {
  /**
   * Enrich company data using AI lookup.
   *
   * Given a company name (and optional partial address), the AI service
   * returns structured company details including industry classification,
   * full address, description, and confidence level.
   *
   * @param data - Company name and optional partial address
   * @returns Enriched company data or error message
   */
  enrichCompany: (
    data: EnrichCompanyRequest,
  ): Promise<AiResponse<EnrichCompanyData>> =>
    aiRequest<EnrichCompanyData>('/api/v1/ai/enrich-company', data),

  /**
   * Generate AI-powered resume content.
   *
   * Supports three content types via discriminated union:
   * - `job_description`: Professional job description from structured input
   * - `summary`: Professional summary / objective statement
   * - `skills_narrative`: Cohesive paragraph from a list of skills
   *
   * @param data - Content type and corresponding context
   * @returns Generated content string or error message
   */
  generateContent: (
    data: GenerateContentRequest,
  ): Promise<AiResponse<GenerateContentData>> =>
    aiRequest<GenerateContentData>('/api/v1/ai/generate-content', data),

  /**
   * Get AI-driven template personalization recommendations.
   *
   * Analyzes the user's profile summary and target role against a specific
   * template to produce actionable recommendations for customization.
   *
   * @param data - Profile summary, target role, and template identifier
   * @returns Array of recommendations or error message
   */
  personalizeTemplate: (
    data: PersonalizeTemplateRequest,
  ): Promise<AiResponse<PersonalizeTemplateData>> =>
    aiRequest<PersonalizeTemplateData>(
      '/api/v1/ai/personalize-template',
      data,
    ),

  /**
   * Score a resume and get structured feedback.
   *
   * Evaluates resume quality with a 0-100 score, summary, categorized
   * feedback items, and optionally missing keywords. Can score against
   * a specific job description.
   *
   * @param data - Resume text and optional job description
   * @returns Scoring result or error message
   */
  scoreResume: (
    data: ScoreResumeRequest,
  ): Promise<AiResponse<ScoreResumeData>> =>
    aiRequest<ScoreResumeData>('/api/v1/ai/score-resume', data),

  /**
   * Generate a professional cover letter.
   *
   * Creates a tailored cover letter from the candidate's resume and
   * target job description, with optional personalization.
   *
   * @param data - Resume text, job description, and optional personalization
   * @returns Generated cover letter text or error message
   */
  generateCoverLetter: (
    data: GenerateCoverLetterRequest,
  ): Promise<AiResponse<GenerateCoverLetterData>> =>
    aiRequest<GenerateCoverLetterData>(
      '/api/v1/ai/generate-cover-letter',
      data,
    ),

  /**
   * Extract polished achievement bullets from raw notes.
   *
   * Converts freeform responsibility notes into structured, quantified
   * achievement bullet points suitable for a resume. Optional job title
   * and company provide context for more relevant results.
   *
   * @param data - Raw notes text with optional job title and company
   * @returns Array of achievement bullet strings or error message
   */
  extractAchievements: (
    data: ExtractAchievementsRequest,
  ): Promise<AiResponse<ExtractAchievementsData>> =>
    aiRequest<ExtractAchievementsData>(
      '/api/v1/ai/extract-achievements',
      data,
    ),
};

// ===========================================================================
// Streaming: Server-Sent Events (SSE)
// ===========================================================================

/**
 * Event yielded by {@link generateCoverLetterStream}.
 *
 * Matches the wire format emitted by `POST /api/v1/ai/generate-cover-letter/stream`.
 */
export type CoverLetterStreamEvent =
  | { type: 'chunk'; text: string }
  | { type: 'done'; total_chars: number; duration_ms: number }
  | { type: 'error'; error: string };

/**
 * Streams a cover letter from the backend SSE endpoint as an async generator.
 *
 * Consumer usage:
 * ```ts
 * for await (const event of generateCoverLetterStream(req, signal)) {
 *   if (event.type === 'chunk') appendText(event.text);
 *   else if (event.type === 'done') markCompleted(event.duration_ms);
 *   else if (event.type === 'error') showError(event.error);
 * }
 * ```
 *
 * Parses the SSE wire format manually (frame = `event: <type>\ndata: <json>\n\n`)
 * using a `TextDecoderStream` + buffer-and-split pattern. Malformed frames are
 * silently skipped so a single bad line cannot break the stream.
 *
 * The server sends the parsed payload as the JSON `data` line, so the `event:`
 * line is redundant on the client — we parse the JSON payload and use its
 * `type` discriminator directly.
 *
 * @param data    - Cover letter generation request payload
 * @param signal  - Optional AbortSignal to cancel the in-flight stream
 * @yields Discriminated union of chunk / done / error events
 * @throws On HTTP error (non-2xx) or network failure before the stream opens
 */
export async function* generateCoverLetterStream(
  data: GenerateCoverLetterRequest,
  signal?: AbortSignal,
): AsyncGenerator<CoverLetterStreamEvent> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch('/api/v1/ai/generate-cover-letter/stream', {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    credentials: 'include',
    signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    log.error('Cover letter stream HTTP error', {
      status: res.status,
      body: text,
    });
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;

      // SSE frames are separated by a blank line (\n\n). Split and keep the
      // trailing partial frame (if any) in the buffer for the next iteration.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        const dataLine = frame
          .split('\n')
          .find((line) => line.startsWith('data: '));
        if (!dataLine) continue;
        const json = dataLine.slice(6);
        try {
          yield JSON.parse(json) as CoverLetterStreamEvent;
        } catch {
          // Skip malformed frames — log at debug level since this is expected
          // occasionally during network hiccups.
          log.debug('Skipping malformed SSE frame', { frame });
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
